import type { ApiClient } from '@momonos/api-client'
import { Card, Loader, useToast } from '@momonos/ui'
import { useEffect, useState } from 'react'

/** FR-ADM-6 — who changed what. */
interface ActivityRow {
  id: string
  action: string
  entityType: string
  entityId: string | null
  actorName: string | null
  beforeJson: string | null
  afterJson: string | null
  createdAt: number
}

export function ActivityScreen({ api, branchId }: { api: ApiClient; branchId: string | null }) {
  const { show } = useToast()
  const [activity, setActivity] = useState<ActivityRow[] | null>(null)

  useEffect(() => {
    api
      .get<{ activity: ActivityRow[] }>('/v1/admin/activity', {
        query: { branch: branchId ?? undefined },
      })
      .then((result) => setActivity(result.activity))
      .catch(() => show('Could not load the activity log', { tone: 'error' }))
  }, [api, branchId, show])

  if (!activity) return <Loader label="Loading activity" />

  return (
    <div className="space-y-4">
      <h1 className="font-bold text-2xl text-content">Activity</h1>

      {activity.length === 0 ? (
        <p className="text-content-muted">Nothing has been changed yet.</p>
      ) : (
        <div className="grid gap-2">
          {activity.map((row) => (
            <Card key={row.id} padded={false} className="p-3">
              <div className="flex items-baseline justify-between gap-3">
                <p className="font-medium text-content">
                  {row.actorName ?? 'System'}{' '}
                  <span className="font-normal text-content-secondary">{row.action}</span>
                </p>
                <time className="shrink-0 text-content-muted text-xs">
                  {new Date(row.createdAt).toLocaleString('en-GB', { timeZone: 'Asia/Dhaka' })}
                </time>
              </div>

              {/* The before/after diff is what makes an entry actionable. */}
              {(row.beforeJson || row.afterJson) && (
                <p className="mt-1 font-mono text-content-muted text-xs">
                  {row.beforeJson && <span>{row.beforeJson}</span>}
                  {row.beforeJson && row.afterJson && <span className="mx-2">→</span>}
                  {row.afterJson && <span className="text-content-secondary">{row.afterJson}</span>}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
